import React, { useEffect, useState } from "react";
import { firebase } from "../../firebase/config";
import "./ProfileNamePrompt.css";

// Nudge a signed-in member who has never set their name.
//
// WHY A NUDGE AND NOT A REDIRECT: src/components/Auth/RequireProfileRedirect.js
// force-redirects nameless users to /complete-profile, but it is not mounted
// anywhere. Turning it on would wall existing paying members out of the site
// until they filled in a form — too blunt for people who have been members for
// months. This asks instead, and can be dismissed.
//
// Dismissal is stored in localStorage with a 14-day snooze rather than
// permanently, so it asks again later without ever becoming a blocker.
const KEY = "bcNamePrompt";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

const snoozed = () => {
  try {
    const v = window.localStorage.getItem(KEY);
    return !!v && Date.now() - Number(v) < SNOOZE_MS;
  } catch (e) {
    return true; // no localStorage (private mode) — don't nag every page load
  }
};

const ProfileNamePrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let unsubscribeDoc;
    const unsubscribeAuth = firebase.auth().onAuthStateChanged((user) => {
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = undefined;
      }
      if (!user || snoozed()) {
        setShow(false);
        return;
      }
      unsubscribeDoc = firebase
        .firestore()
        .collection("membersData")
        .doc(user.uid)
        .onSnapshot(
          (snapshot) => {
            const d = snapshot.exists ? snapshot.data() : null;
            const first = (d && d.firstName ? String(d.firstName) : "").trim();
            const last = (d && d.surname ? String(d.surname) : "").trim();
            // Google sign-ups carry their name as displayName (no firstName/surname) - that counts as a name.
            const shown = String((d && d.displayName) || user.displayName || "").trim();
            setShow(!first && !last && !shown);
          },
          // Can't read the doc -> say nothing rather than nag wrongly.
          () => setShow(false)
        );
    });
    return () => {
      if (unsubscribeDoc) unsubscribeDoc();
      unsubscribeAuth();
    };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(KEY, String(Date.now()));
    } catch (e) {
      /* private mode — hide for this session only */
    }
    setShow(false);
  };

  return (
    <div className="ProfileNamePrompt" role="status">
      <div className="ProfileNamePrompt__inner">
        <span className="ProfileNamePrompt__text">
          We don&rsquo;t have your name on your account.
        </span>
        <a className="ProfileNamePrompt__cta" href="/complete-profile">
          Add your name
        </a>
        <button
          type="button"
          className="ProfileNamePrompt__close"
          onClick={dismiss}
          aria-label="Not now"
        >
          &times;
        </button>
      </div>
    </div>
  );
};

export default ProfileNamePrompt;
